import React from 'react';
import Button from '@material-ui/core/Button';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import { pad, renderDate, renderMoney, dateRangeFilter } from '../../../common/services/util';
import DateRangeFilter from 'common/components/DateRangeFilter';
import SelectField from 'common/components/SelectField';
import StoreIcon from '@material-ui/icons/Store';
import BuildIcon from '@material-ui/icons/Build';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

export const ESTADOS = {
    stock:      'En stock',
    reparacion: 'Reparando',
    reservado:  'Reservado',
    vendido:    'Vendido',
};

export const ESTADO_COLORS = {
    stock:     { bg: '#e8f5e9', color: '#2e7d32' },
    reparacion:{ bg: '#fff3e0', color: '#e65100' },
    reservado: { bg: '#e3f2fd', color: '#1565c0' },
    vendido:   { bg: '#f5f5f5', color: '#757575' },
};

export const ESTADO_ICONS = {
    stock:      StoreIcon,
    reparacion: BuildIcon,
    reservado:  BookmarkIcon,
    vendido:    CheckCircleIcon,
};

export const ESTADO_COLOR_MAP = {
    stock:      '#2e7d32',
    reparacion: '#e65100',
    reservado:  '#1565c0',
    vendido:    '#757575',
};

export const GRADO_COLORS = {
    A: '#2e7d32',
    B: '#f57c00',
    C: '#c62828',
    D: '#9e9e9e',
};

// Extrae el número de chip Apple Silicon (M1→10, M2→20, ..., M5→50) de forma future-proof
const getMChipScore = (m) => {
    const match = m.match(/\bm(\d+)\b/);
    return match ? parseInt(match[1]) * 10 : 0;
};

export const getModelSortKey = (modelo) => {
    if (!modelo) return -1;
    const m = String(modelo).toLowerCase().trim();

    const iPhoneScore = () => {
        const getVariant = () => {
            if (m.includes('pro max')) return 5;
            if (m.includes('pro'))     return 4;
            if (m.includes('plus'))    return 3;
            if (m.includes(' air'))    return 2; // iPhone Air
            if (m.includes('mini'))    return 0;
            return 1; // estándar
        };
        // SE: posicionado por generación equivalente (con o sin "iPhone")
        if (/\bse\b/.test(m)) {
            const is3rd = m.includes('3') || /tercer|third/.test(m);
            const is2nd = m.includes('2') || /segundo|second/.test(m);
            return 30000 + (is3rd ? 14 : is2nd ? 12 : 6) * 10;
        }
        // Número de generación: "iPhone 17 Pro Max" o abreviado "17 Pro Max"
        const numMatch = m.match(/iphone\s+(\d{1,2})/) || m.match(/^(\d{1,2})(?:\s|$)/);
        if (numMatch) return 30000 + parseInt(numMatch[1]) * 10 + getVariant();
        return 30000;
    };

    // === iPhone (base 30000) ===
    if (m.includes('iphone')) return iPhoneScore();

    // === Mac (base 20000): chip domina, tipo como desempate ===
    if (m.includes('mac')) {
        const chip = getMChipScore(m); // M1→10, M2→20, ..., future-proof
        let type = 0;
        if (m.includes('mac pro'))          type = 5;
        else if (m.includes('imac'))        type = 4;
        else if (m.includes('mac studio'))  type = 3;
        else if (m.includes('macbook pro')) type = 2;
        else if (m.includes('macbook air')) type = 1;
        // mac mini: type = 0
        return 20000 + chip * 10 + type;
    }

    // === iPad (base 10000): familia domina, luego chip, luego generación ===
    if (m.includes('ipad')) {
        let family = 1; // iPad estándar
        if (m.includes('pro'))       family = 3;
        else if (m.includes('air'))  family = 2;
        else if (m.includes('mini')) family = 0;
        const chip = getMChipScore(m);
        let gen = 0;
        if (!chip) {
            const genMatch = m.match(/(\d+)(?:ª|a|th|st|nd|rd)/);
            if (genMatch) gen = parseInt(genMatch[1]);
        }
        return 10000 + family * 1000 + chip * 10 + gen;
    }

    // === Modelo abreviado sin "iPhone": "17 Pro Max", "16", "SE (3ª gen)" ===
    if (/^(se\b|\d{1,2}(\s|$))/.test(m)) return iPhoneScore();

    return 0; // Otros dispositivos
};

const textArea = props => (
    <textarea
        style={{ width: '100%', fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}
        rows="2"
        value={props.value || ''}
        onChange={e => props.onChange(e.target.value)}
    />
);

export const getColumns = (onStateChange, orderColumns = {}) => {
const cols = [
    {
        title: 'Referencia', field: 'reference', editable: 'never', filtering: false,
        cellStyle: { whiteSpace: 'nowrap' },
        render: r => {
            if (!r) return null;
            return (
                <Button
                    variant="outlined"
                    size="small"
                    style={{
                        fontWeight: 'bold',
                        color:            r._selected ? '#c62828' : 'black',
                        backgroundColor:  r._selected ? '#FDE0EA' : 'transparent',
                        border:           r._selected ? '2px solid #c62828' : '2px solid black',
                        borderRadius: 6,
                        minWidth: 60,
                        padding: '2px 8px',
                        lineHeight: 1.4,
                    }}
                    disableRipple
                    disableFocusRipple
                >
                    {r.reference}
                </Button>
            );
        },
    },
    {
        title: 'Modelo', field: 'modelo',
        defaultSort: 'desc',
        customSort: (a, b) => getModelSortKey(a.modelo) - getModelSortKey(b.modelo),
        cellStyle: { minWidth: 150 },
        headerStyle: { minWidth: 150 },
        render: r => {
            if (!r) return null;
            const hasDoneTest = r.testResults && Object.keys(r.testResults).length > 0;
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {hasDoneTest && (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: '#fff', borderRadius: 4, padding: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.25)', flexShrink: 0,
                        }}>
                            <AssignmentTurnedInIcon style={{ fontSize: 14, color: '#43a047' }} />
                        </span>
                    )}
                    {r.modelo || ''}
                </div>
            );
        },
    },
    { title: 'Capacidad',        field: 'capacidad' },
    { title: 'Procesador',       field: 'procesador' },
    { title: 'RAM',              field: 'ram', width: 45 },
    { title: 'Color',            field: 'color', width: 110 },
    {
        title: 'Grado', field: 'grado',
        lookup: { A: 'A', B: 'B', C: 'C', D: 'D' },
        render: r => (r && r.grado)
            ? <span style={{ fontWeight: 700, color: GRADO_COLORS[r.grado] || '#333' }}>{r.grado}</span>
            : '',
    },
    {
        title: 'Condición', field: 'condicion',
        lookup: { usado: 'Usado', premium: 'Premium' },
        render: r => {
            if (!r || !r.condicion) return '';
            const isPremium = r.condicion === 'premium';
            return (
                <span style={{
                    fontWeight: 600,
                    color: isPremium ? '#b8860b' : '#555',
                }}>
                    {isPremium ? 'Premium' : 'Usado'}
                </span>
            );
        },
    },
    {
        title: 'Salud (%)', field: 'salud', width: 50,
        render: r => {
            if (!r || r.salud == null || r.salud === '') return '';
            const v = Number(r.salud);
            const color = v >= 90 ? '#2e7d32' : v >= 80 ? '#f9a825' : '#c62828';
            return <span style={{ fontWeight: 700, color }}>{v}%</span>;
        },
    },
    { title: 'Ciclos',           field: 'ciclos' },
    { title: 'Problema original',field: 'problemaOriginal',   editComponent: textArea },
    {
        title: 'Precio', field: 'precio', type: 'numeric',
        render: r => (r && r.precio != null) ? renderMoney(r.precio) : '',
    },
    {
        title: 'Precio s/IVA', field: 'precioSinIva', type: 'numeric',
        render: r => (r && r.precioSinIva != null) ? renderMoney(r.precioSinIva) : '',
    },
    {
        title: 'Precio compra', field: 'precioCompra', type: 'numeric',
        render: r => (r && r.precioCompra != null) ? renderMoney(r.precioCompra) : '',
    },
    {
        title: 'Compra s/IVA', field: 'precioCompraSinIva', type: 'numeric',
        render: r => (r && r.precioCompraSinIva != null) ? renderMoney(r.precioCompraSinIva) : '',
    },
    {
        title: 'Beneficio', field: 'beneficio', editable: 'never', filtering: false,
        render: r => {
            if (!r || r.precioSinIva == null || r.precioCompraSinIva == null) return '';
            const b = r.precioSinIva - r.precioCompraSinIva;
            return (
                <span style={{ fontWeight: 600, color: b >= 0 ? '#2e7d32' : '#c62828' }}>
                    {renderMoney(b)}
                </span>
            );
        },
    },
    { title: 'IMEI',             field: 'imei' },
    { title: 'Serial',           field: 'serial' },
    {
        title: 'Estado', field: 'estado',
        lookup: ESTADOS,
        editable: 'never',
        render: r => {
            if (!r) return null;
            return (
                <SelectField
                    mapData={ESTADOS}
                    value={r.estado || 'stock'}
                    iconMap={ESTADO_ICONS}
                    colorMap={ESTADO_COLOR_MAP}
                    handleChangeValue={newVal => onStateChange && onStateChange(r, newVal)}
                />
            );
        },
    },
    {
        title: 'Vendido', field: 'vendido', type: 'boolean', hidden: true,
        render: r => (r && r.vendido) ? 'Sí' : 'No',
    },
    {
        title: 'Fecha entrada', field: 'fechaEntrada', editable: 'never',
        filterComponent: props => <DateRangeFilter {...props} />,
        customFilterAndSearch: (filter, r) => r ? dateRangeFilter(filter, r.fechaEntrada) : false,
        render: r => {
            if (!r || !r.fechaEntrada) return '';
            const [date, time] = renderDate(r.fechaEntrada, true).split(' ');
            return <div style={{ textAlign: 'center' }}><div>{date}</div><div style={{ fontSize: 11, color: '#757575' }}>{time}</div></div>;
        },
    },
    {
        title: 'Fecha venta', field: 'fechaVenta', editable: 'never',
        filterComponent: props => <DateRangeFilter {...props} />,
        customFilterAndSearch: (filter, r) => r ? dateRangeFilter(filter, r.fechaVenta) : false,
        render: r => {
            if (!r || !r.fechaVenta) return '';
            const [date, time] = renderDate(r.fechaVenta, true).split(' ');
            return <div style={{ textAlign: 'center' }}><div>{date}</div><div style={{ fontSize: 11, color: '#757575' }}>{time}</div></div>;
        },
    },
    { title: 'Observaciones 1',  field: 'observaciones1',     editComponent: textArea },
    { title: 'Observaciones 2',  field: 'observaciones2',     editComponent: textArea },
];
const ord = orderColumns || {};
return cols.map(col => {
    const columnOrder = ord[col.field];
    if (columnOrder == null) return col;
    return { ...col, tableData: { columnOrder } };
});
};

export const applyDefaultReac = (reac, count) => {
    reac.lastUpdated = new Date().getTime();
    reac.fechaEntrada = reac.fechaEntrada || new Date().getTime();
    reac.reference = pad(count + 1, 4);
    reac.estado = reac.estado || 'reparacion';
};

export const getRowStyle = (rowData, index) => {
    if (rowData && rowData._selected) {
        return { backgroundColor: '#e3f2fd', outline: '2px solid #1565C0' };
    }
    const cfg = rowData && ESTADO_COLORS[rowData.estado];
    if (cfg) return { backgroundColor: cfg.bg };
    return index % 2 === 0 ? {} : { backgroundColor: '#fafafa' };
};
